'use client';

import { useState, useEffect, useRef } from 'react';
import { Search, CheckCircle, XCircle, AlertTriangle, Zap, ArrowRight, Loader2, Globe, X, Calendar } from 'lucide-react';
import Link from 'next/link';

const CALENDLY_URL = 'https://calendly.com/mactivated90';

interface DiagnosticCheck {
  label: string;
  pass: boolean;
  detail: string;
}

interface DiagnosticResult {
  url: string;
  score: number;
  status: 'green' | 'red';
  checks: DiagnosticCheck[];
  loadTime: number;
  issues: string[];
}

function CalendlyModal({ onClose }: { onClose: () => void }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl bg-slate-900 rounded-2xl border border-slate-700 overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-green-400" />
            <span className="text-white font-semibold text-sm">Schedule Your Free Fix Call</span>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="h-[580px]">
          <iframe
            ref={iframeRef}
            src={`${CALENDLY_URL}?embed_domain=${typeof window !== 'undefined' ? window.location.hostname : ''}&embed_type=Inline&hide_event_type_details=0&hide_gdpr_banner=1`}
            width="100%"
            height="100%"
            frameBorder="0"
            title="Schedule a call with Bloodhound"
          />
        </div>
      </div>
    </div>
  );
}

export default function DiagnosticPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      setUtmSource(params.get('utm_source') || 'diagnostic');
      setUtmMedium(params.get('utm_medium') || 'web');
      setUtmCampaign(params.get('utm_campaign') || 'site-scan');
    }
  }, []);

  const runDiagnostic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    setShowBanner(false);

    try {
      const res = await fetch('/api/diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: url.trim(),
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Diagnostic failed');
      setResult(data);
      if (data.status === 'red') {
        setTimeout(() => setShowBanner(true), 600);
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCalendly = () => setShowCalendly(true);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient grid */}
      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(34,197,94,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Calendly modal */}
      {showCalendly && <CalendlyModal onClose={() => setShowCalendly(false)} />}

      {/* Red banner */}
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-red-600 text-white px-6 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle size={18} className="shrink-0" />
            <span className="font-semibold text-sm">
              🔴 Your site has critical issues — Hound can fix this in 48 hours.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openCalendly}
              className="bg-white text-red-600 font-bold text-xs px-4 py-1.5 rounded-full hover:bg-red-50 transition-colors flex items-center gap-1.5"
            >
              Schedule Fix <ArrowRight size={12} />
            </button>
            <button
              type="button"
              onClick={() => setShowBanner(false)}
              className="text-white/70 hover:text-white text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className={`relative max-w-3xl mx-auto px-6 py-16 ${showBanner ? 'pt-28' : ''}`}>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-green-400 text-xs font-semibold tracking-wide uppercase">
              Free Site Diagnostic
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Is your site a{' '}
            <span className="text-red-400">GeoCities fossil</span>?
          </h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Drop any URL below. We'll screen it in seconds — speed, SEO, mobile, security. Green means you're good. Red means you need Hound.
          </p>
        </div>

        {/* Input */}
        <form onSubmit={runDiagnostic} className="flex gap-3 mb-10">
          <div className="flex-1 relative">
            <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="yoursite.com"
              className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-4 text-white placeholder-slate-500 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-4 rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? 'Scanning...' : 'Scan Site'}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div className="bg-red-950/40 border border-red-800/50 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 animate-pulse">
            <div className="h-6 bg-slate-800 rounded w-1/3 mb-4" />
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-slate-800 rounded-full" />
                  <div className="h-4 bg-slate-800 rounded flex-1" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="fade-in">
            {/* Score card */}
            <div className={`rounded-2xl border p-6 mb-6 ${result.status === 'green' ? 'bg-green-950/30 border-green-800/50' : 'bg-red-950/30 border-red-800/50'}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mb-1">Diagnostic Result</p>
                  <p className="text-slate-300 text-sm truncate max-w-xs">{result.url}</p>
                </div>
                <div className="text-right">
                  <div className={`text-5xl font-bold ${result.status === 'green' ? 'text-green-400' : 'text-red-400'}`}>
                    {result.score}
                  </div>
                  <div className="text-slate-500 text-xs">/100</div>
                </div>
              </div>

              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${result.status === 'green' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {result.status === 'green' ? (
                  <><CheckCircle size={14} /> Site looks healthy</>
                ) : (
                  <><XCircle size={14} /> Site needs work</>
                )}
              </div>

              <div className="flex items-center gap-2 mt-3 text-slate-400 text-xs">
                <Zap size={12} className="text-yellow-400" />
                Load time: {result.loadTime}ms
              </div>
            </div>

            {/* Checks */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 mb-6">
              <h2 className="text-sm font-semibold text-slate-300 mb-4 uppercase tracking-widest">Checks</h2>
              <div className="space-y-3">
                {result.checks.map((check, i) => (
                  <div key={i} className="flex items-start gap-3">
                    {check.pass ? (
                      <CheckCircle size={16} className="text-green-400 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${check.pass ? 'text-slate-200' : 'text-slate-300'}`}>{check.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{check.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA for red */}
            {result.status === 'red' && (
              <div className="bg-gradient-to-r from-red-950/60 to-slate-900 border border-red-800/40 rounded-2xl p-6 text-center">
                <p className="text-red-400 font-bold text-lg mb-1">🔴 Hound can fix this in 48 hours.</p>
                <p className="text-slate-400 text-sm mb-4">
                  {result.issues.length} issue{result.issues.length !== 1 ? 's' : ''} found. We'll handle every one of them — fast.
                </p>
                <button
                  onClick={openCalendly}
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-3 rounded-xl transition-all active:scale-95"
                >
                  <Calendar size={16} />
                  Schedule a Fix Call <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* CTA for green */}
            {result.status === 'green' && (
              <div className="bg-green-950/30 border border-green-800/40 rounded-2xl p-6 text-center">
                <p className="text-green-400 font-bold text-lg mb-1">✅ Looking solid!</p>
                <p className="text-slate-400 text-sm mb-4">
                  Your site passes the basics. Want to go deeper — SEO, conversions, automation?
                </p>
                <button
                  onClick={openCalendly}
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-3 rounded-xl transition-all active:scale-95"
                >
                  <Calendar size={16} />
                  Let's Talk <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer link */}
        <div className="text-center mt-12">
          <Link href="/" className="text-slate-500 hover:text-slate-400 text-sm transition-colors">
            ← Back to Bloodhound
          </Link>
        </div>
      </div>
    </div>
  );
}
